import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MerchantVouchers from "../components/MerchantVouchers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PlanPill, OuroBadge, CashbackBadge } from "../components/Badges";
import { Coins, MapPin, Plus, Edit, Search, Camera, Wallet as WalletIcon, Code2, ShoppingCart, Gift, Clock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CompanyEditDialog } from "./AdminCompanies";
import MerchantProducts from "../components/MerchantProducts";
import MerchantRedeem from "../components/MerchantRedeem";
import CompanyWallet from "../components/CompanyWallet";
import QrScanner from "../components/QrScanner";
import MerchantReports from "../components/MerchantReports";
import MerchantDevApi from "../components/MerchantDevApi";
import ImageInput from "../components/ImageInput";
import SegmentsPicker from "../components/SegmentsPicker";
import LocationPicker from "../components/LocationPicker";

export default function MerchantDashboard() {
  const { user } = useAuth();
  const loc = useLocation();
  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", category: "", city_slug: "", lat: "", lng: "", address: "", logo_url: "", cover_url: "", accepts_cashback: false, segments: [], cnpj: "", pix_key: "" });
  const [txForm, setTxForm] = useState({ wallet_code: "", company_id: "", purchase_value: "" });
  const [txCustomer, setTxCustomer] = useState(null);
  const [txPreview, setTxPreview] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanFor, setScanFor] = useState(null); // null | "cashback"
  // Duplicate detection during onboarding
  const [similar, setSimilar] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  // Global company selector (shared filter across all tabs)
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => localStorage.getItem("mb_selected_company") || "");

  const refresh = () => {
    api.get("/companies/owner/mine").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/cashback/my-issued").then((r) => setPending(r.data)).catch(() => {});
  };

  useEffect(() => {
    refresh();
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/payments/plans").then((r) => setPlans(r.data));
    api.get("/company-categories?active=true").then((r) => setCategoriesList(r.data || [])).catch(() => {});
  }, []);

  // Auto-verify pending payments when returning from Mercado Pago (?payment=success|pending|subscription=success)
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const paymentStatus = params.get("payment");
    const subStatus = params.get("subscription");
    if (!paymentStatus && !subStatus) return;

    if (paymentStatus === "failure") {
      toast.error("Pagamento não concluído. Tente novamente.");
      window.history.replaceState({}, "", "/merchant");
      return;
    }

    // Clean URL right away so polling doesn't trigger again
    window.history.replaceState({}, "", "/merchant");

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 18; // 18 * 5s = 90s total
    const t = toast.loading("Confirmando pagamento, aguarde...");

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const before = await api.get("/companies/owner/mine").then((r) => r.data).catch(() => null);
        const beforeMap = new Map((before || []).map((c) => [c.id, c.plan]));

        const { data } = await api.post("/payments/check-pending");
        if (data.applied && data.applied.length > 0) {
          toast.dismiss(t);
          toast.success(`Plano ${data.applied[0].plan_tier} ativado com sucesso!`);
          refresh();
          return;
        }

        // Even if check-pending didn't apply (e.g. webhook already did),
        // detect plan change via owner/mine before/after
        const after = await api.get("/companies/owner/mine").then((r) => r.data).catch(() => null);
        if (after) {
          for (const c of after) {
            const prev = beforeMap.get(c.id);
            if (prev && prev !== c.plan && c.plan !== "GRATIS") {
              toast.dismiss(t);
              toast.success(`Plano atualizado para ${c.plan}!`);
              setCompanies(after);
              return;
            }
          }
        }
      } catch {}

      if (attempts >= maxAttempts) {
        toast.dismiss(t);
        toast.message("Pagamento ainda em processamento. Atualize a página em 1 minuto ou clique em Verificar pagamento.");
        return;
      }
      setTimeout(poll, 5000);
    };

    if (subStatus === "success") {
      toast.dismiss(t);
      toast.success("Assinatura recorrente autorizada! Aguardando ativação...");
      // Start polling anyway since first charge might take a moment
      setTimeout(() => { refresh(); poll(); }, 3000);
    } else {
      // payment=success|pending
      poll();
    }

    return () => { cancelled = true; toast.dismiss(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  const checkPendingPayments = async () => {
    setBusy(true);
    const t = toast.loading("Consultando Mercado Pago...");
    try {
      const { data } = await api.post("/payments/check-pending");
      toast.dismiss(t);
      if (data.applied && data.applied.length > 0) {
        toast.success(`${data.applied.length} pagamento(s) confirmado(s)! Plano(s) ativado(s).`);
        refresh();
      } else if (data.checked === 0) {
        toast.message("Nenhum pagamento pendente recente para verificar.");
      } else {
        toast.message(`${data.checked} preferência(s) verificada(s) — nenhuma aprovada ainda.`);
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao consultar");
    } finally { setBusy(false); }
  };

  // Default selected company to first owned (if none stored)
  useEffect(() => {
    if (!selectedCompanyId && companies?.[0]?.id) {
      setSelectedCompanyId(companies[0].id);
      localStorage.setItem("mb_selected_company", companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  const onSelectCompany = (v) => {
    setSelectedCompanyId(v);
    localStorage.setItem("mb_selected_company", v);
  };

  // Filtered list of companies to render in each tab (1 if selected, all otherwise)
  const visibleCompanies = selectedCompanyId
    ? companies.filter((c) => c.id === selectedCompanyId)
    : companies;

  // Debounced dup-detection while merchant types the new company name
  useEffect(() => {
    if (!creating) { setSimilar([]); return; }
    const q = (form.name || "").trim();
    if (q.length < 3) { setSimilar([]); return; }
    setSimilarLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ name: q });
        if (form.city_slug) params.set("city_slug", form.city_slug);
        const r = await api.get(`/companies/find-similar?${params.toString()}`);
        setSimilar(r.data || []);
      } catch { setSimilar([]); }
      finally { setSimilarLoading(false); }
    }, 500);
    return () => clearTimeout(handle);
  }, [form.name, form.city_slug, creating]);

  const claimCompany = async (companyId) => {
    if (!window.confirm("Reivindicar esta empresa? Após isso ela passa a ser sua e você poderá complementar os dados.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/companies/${companyId}/claim`);
      toast.success("Empresa reivindicada! Complemente os dados abaixo.");
      setCreating(false);
      setSimilar([]);
      refresh();
      // Open the edit dialog with the claimed company
      setEditing(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao reivindicar"); }
    finally { setBusy(false); }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        cnpj: form.cnpj ? form.cnpj.replace(/[^0-9]/g, "") : null,
        pix_key: form.pix_key || null,
      };
      await api.post("/companies", payload);
      toast.success("Empresa criada!");
      setCreating(false);
      setForm({ name: "", description: "", category: "", city_slug: "", lat: "", lng: "", address: "", logo_url: "", cover_url: "", accepts_cashback: false, segments: [], cnpj: "", pix_key: "" });
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const subscribe = async (companyId, planTier) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/preferences", { company_id: companyId, plan_tier: planTier });
      const url = data.init_point || data.sandbox_init_point;
      if (url) {
        toast.success("Redirecionando ao Mercado Pago...");
        // Use same tab so back_urls fire reliably and we can auto-verify on return
        window.location.href = url;
      }
      else toast.error("URL do checkout não recebida");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const subscribeRecurring = async (companyId, planTier) => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/create", { company_id: companyId, plan_tier: planTier });
      const url = data.init_point;
      if (url) {
        toast.success("Redirecionando para autorizar débito recorrente...");
        window.location.href = url;
      } else toast.error("URL da assinatura não recebida");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Dialog: choose between one-time payment vs recurring
  const [planDialog, setPlanDialog] = useState(null); // { companyId, plan }
  const openPlanDialog = (companyId, plan) => setPlanDialog({ companyId, plan });
  const closePlanDialog = () => setPlanDialog(null);

  // Change plan flow (upgrade pro-rata / downgrade scheduled)
  const [changePlanFor, setChangePlanFor] = useState(null); // { company, plan }

  // Subscription alerts (in-app banner)
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    api.get("/subscription-alerts/mine").then((r) => setAlerts(r.data || [])).catch(() => {});
  }, []);
  const dismissAlert = async (id) => {
    setAlerts((arr) => arr.filter((a) => a.id !== id));
    try { await api.post(`/subscription-alerts/${id}/read`); } catch {}
  };
  const unreadAlerts = alerts.filter((a) => !a.read);

  const lookupCustomer = async (codeArg) => {
    const code = codeArg || txForm.wallet_code;
    if (!code) return;
    try {
      const { data } = await api.get(`/cashback/lookup?code=${encodeURIComponent(code)}`);
      setTxCustomer(data);
      toast.success(`${data.name} · saldo R$ ${data.balance.toFixed(2)}`);
    } catch (e) { setTxCustomer(null); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const registerCashback = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/cashback/transaction", {
        wallet_code: txForm.wallet_code,
        company_id: txForm.company_id,
        purchase_value: parseFloat(txForm.purchase_value),
      });
      toast.success(`Cashback de R$ ${data.cashback_value.toFixed(2)} creditado a ${data.customer_name}. Saldo da empresa: R$ ${data.company_wallet_balance.toFixed(2)}`);
      setTxForm({ wallet_code: "", company_id: "", purchase_value: "" });
      setTxPreview(null);
      setTxCustomer(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const refreshPreview = async (company_id, purchase_value) => {
    if (!company_id || !purchase_value || parseFloat(purchase_value) <= 0) { setTxPreview(null); return; }
    try {
      const { data } = await api.get(`/cashback/preview?company_id=${company_id}&purchase_value=${purchase_value}`);
      setTxPreview(data);
    } catch { setTxPreview(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {user?.role === "MERCHANT" && user?.approved === false && (
        <Card className="p-5 rounded-2xl border-2 border-amber-300 bg-amber-50 mb-6" data-testid="approval-banner">
          <div className="flex items-center gap-3">
            <Clock className="text-amber-700 shrink-0" size={28}/>
            <div>
              <h2 className="font-display text-lg font-bold text-amber-900">Sua conta está em análise</h2>
              <p className="text-sm text-amber-800 mt-0.5">{user?.email_verified ? "Email confirmado!" : "Confirme seu email primeiro."} Aguardando aprovação do administrador para liberar cadastro de empresas e vendas.</p>
              {!user?.email_verified && <Link to={`/verify-email?email=${encodeURIComponent(user?.email || '')}`} className="text-sm text-blue-900 font-bold hover:underline mt-2 inline-block">Confirmar email →</Link>}
            </div>
          </div>
        </Card>
      )}

      {unreadAlerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="subscription-alerts">
          {unreadAlerts.map((a) => (
            <Card key={a.id}
              className={`p-4 rounded-2xl border-2 ${
                a.type === "expired" ? "border-red-300 bg-red-50" :
                a.type === "pending_sub" ? "border-amber-300 bg-amber-50" :
                "border-orange-300 bg-orange-50"
              }`}
              data-testid={`alert-${a.type}`}
            >
              <div className="flex items-start gap-3">
                <Clock className={`shrink-0 mt-0.5 ${a.type === "expired" ? "text-red-700" : a.type === "pending_sub" ? "text-amber-700" : "text-orange-700"}`} size={22}/>
                <div className="flex-1">
                  <h3 className={`font-display text-sm font-bold ${a.type === "expired" ? "text-red-900" : a.type === "pending_sub" ? "text-amber-900" : "text-orange-900"}`}>
                    {a.title}
                  </h3>
                  <p className="text-xs mt-1 opacity-80">{a.message}</p>
                  {a.init_point && a.type === "pending_sub" && (
                    <a href={a.init_point} className="inline-block mt-2 text-xs font-bold text-blue-900 hover:underline" data-testid={`alert-action-${a.id}`}>
                      Autorizar agora no Mercado Pago →
                    </a>
                  )}
                </div>
                <button onClick={() => dismissAlert(a.id)} className="text-stone-400 hover:text-stone-600 text-xs" data-testid={`dismiss-alert-${a.id}`}>✕</button>
              </div>
            </Card>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">Painel do Comerciante</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Gerencie seu negócio, carteira e cashback.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          {companies.length > 1 && (
            <div className="min-w-[200px]">
              <label className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Empresa</label>
              <Select value={selectedCompanyId} onValueChange={onSelectCompany}>
                <SelectTrigger className="rounded-xl mt-1 h-10 bg-blue-50 border-blue-200" data-testid="merchant-company-select">
                  <SelectValue placeholder="Todas"/>
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Link to="/merchant/packages">
            <Button variant="outline" className="rounded-xl h-10" data-testid="nav-packages-btn">
              <ShoppingCart size={16} className="mr-1.5"/> Lojinha de Pacotes
            </Button>
          </Link>
          <Button onClick={checkPendingPayments} disabled={busy} variant="outline" className="rounded-xl h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="check-pending-payments-btn">
            <Clock size={16} className="mr-1.5"/> Verificar pagamento
          </Button>
          <Button onClick={() => setCreating(!creating)} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl h-10" data-testid="new-company-btn">
            <Plus size={16} className="mr-1.5"/> Nova empresa
          </Button>
        </div>
      </div>

      {creating && (
        <Card className="mb-8 p-6 rounded-2xl border-stone-200">
          <h2 className="font-display text-xl font-semibold mb-4">Cadastrar empresa</h2>
          <form onSubmit={createCompany} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Logo (URL ou upload)</Label>
              <div className="mt-1"><ImageInput value={form.logo_url} onChange={(v) => setForm({...form, logo_url: v})} aspect="1/1" testid="cf-logo"/></div>
            </div>
            <div className="sm:col-span-2"><Label>Imagem de capa (16:8, destaques e mapa)</Label>
              <div className="mt-1"><ImageInput value={form.cover_url} onChange={(v) => setForm({...form, cover_url: v})} aspect="16/8" testid="cf-cover"/></div>
            </div>
            <div className="sm:col-span-2"><SegmentsPicker value={form.segments} onChange={(v) => setForm({...form, segments: v})} testid="cf-segments"/></div>
            <div><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" data-testid="cf-name"/></div>
            <div><Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="cf-category"><SelectValue placeholder="Selecione"/></SelectTrigger>
                <SelectContent className="max-h-72">
                  {categoriesList.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Duplicate detection panel */}
            {similar.length > 0 && (
              <div className="sm:col-span-2 p-4 rounded-2xl border-2 border-amber-300 bg-amber-50" data-testid="cf-similar-panel">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-2">
                  Encontramos {similar.length} empresa{similar.length > 1 ? "s" : ""} parecida{similar.length > 1 ? "s" : ""}. Alguma é a sua?
                </div>
                <div className="space-y-2">
                  {similar.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-2 rounded-xl bg-white border border-stone-200" data-testid={`cf-similar-${s.id}`}>
                      {s.logo_url && <img src={s.logo_url} alt="" className="w-10 h-10 rounded-lg object-contain bg-stone-50"/>}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-stone-900 truncate">{s.name}</div>
                        <div className="text-[11px] text-stone-500 flex gap-2 truncate">
                          {s.city_slug && <span>{s.city_slug}</span>}
                          {s.address && <span className="truncate">{s.address}</span>}
                          {s.plan && <span className="uppercase font-bold">{s.plan}</span>}
                        </div>
                      </div>
                      {s.claimable ? (
                        <Button type="button" size="sm" onClick={() => claimCompany(s.id)} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-amber-950 rounded-lg" data-testid={`cf-claim-${s.id}`}>
                          Esta é minha
                        </Button>
                      ) : (
                        <span className="text-[10px] text-stone-400 font-bold uppercase">Já reivindicada</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-stone-600 mt-2">
                  Nenhuma é sua? Continue preenchendo o formulário abaixo pra criar uma nova.
                </div>
              </div>
            )}
            {similarLoading && (
              <div className="sm:col-span-2 text-xs text-stone-400" data-testid="cf-similar-loading">Buscando empresas similares...</div>
            )}
            <div className="sm:col-span-2"><Label>Descrição</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-xl mt-1" data-testid="cf-desc"/></div>
            <div className="sm:col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="rounded-xl mt-1" data-testid="cf-address"/></div>
            <div>
              <Label>Cidade</Label>
              <Select value={form.city_slug} onValueChange={(v) => {
                const c = cities.find((x) => x.slug === v);
                setForm({...form, city_slug: v, lat: c?.lat.toString() || "", lng: c?.lng.toString() || ""});
              }}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="cf-city"><SelectValue placeholder="Selecione"/></SelectTrigger>
                <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:col-span-2">
              <div><Label>Latitude</Label><Input required value={form.lat} onChange={(e) => setForm({...form, lat: e.target.value})} className="rounded-xl mt-1" data-testid="cf-lat"/></div>
              <div><Label>Longitude</Label><Input required value={form.lng} onChange={(e) => setForm({...form, lng: e.target.value})} className="rounded-xl mt-1" data-testid="cf-lng"/></div>
            </div>
            <div className="sm:col-span-2">
              <LocationPicker
                value={{ lat: parseFloat(form.lat), lng: parseFloat(form.lng) }}
                onChange={(p) => setForm({ ...form, lat: p.lat.toFixed(6), lng: p.lng.toFixed(6), address: p.address || form.address })}
                label="📍 Selecionar localização no mapa"
                buttonClass="w-full bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              />
            </div>
            <div><Label>CNPJ <span className="text-stone-400 text-[10px]">(opcional, formato 00.000.000/0000-00)</span></Label>
              <Input value={form.cnpj} onChange={(e) => setForm({...form, cnpj: e.target.value})} placeholder="00.000.000/0000-00" className="rounded-xl mt-1" data-testid="cf-cnpj"/>
            </div>
            <div><Label>Chave PIX <span className="text-stone-400 text-[10px]">(para recebimento de saques)</span></Label>
              <Input value={form.pix_key} onChange={(e) => setForm({...form, pix_key: e.target.value})} placeholder="email, CPF/CNPJ, telefone ou chave aleatória" className="rounded-xl mt-1" data-testid="cf-pix-key"/>
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="cf-submit">{busy ? "Salvando..." : "Salvar"}</Button>
            </div>
          </form>
        </Card>
      )}

      <Tabs defaultValue="companies" className="mt-2">
        <TabsList className="bg-stone-100 rounded-xl flex-wrap h-auto">
          <TabsTrigger value="companies" data-testid="tab-companies">Minhas empresas</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet"><WalletIcon size={14} className="mr-1"/>Carteira da Empresa</TabsTrigger>
          <TabsTrigger value="cashback" data-testid="tab-cashback">Conceder cashback</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="tab-vouchers"><Gift size={14} className="mr-1"/>Vouchers</TabsTrigger>
          <TabsTrigger value="redeem" data-testid="tab-redeem">Venda Direta</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produtos & Serviços</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Relatórios</TabsTrigger>
          <TabsTrigger value="dev" data-testid="tab-dev"><Code2 size={14} className="mr-1"/>Dev / API</TabsTrigger>
        </TabsList>

        <TabsContent value="companies" className="mt-6 space-y-4">
          {companies.length === 0 && <p className="text-stone-500">Você ainda não cadastrou empresas.</p>}
          {visibleCompanies.map((c) => (
            <Card key={c.id} className={`p-6 rounded-2xl border ${c.plan === "OURO" ? "border-amber-400 ouro-glow" : "border-stone-200"}`} data-testid={`merchant-company-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2.5 mb-1">
                    <div className="w-12 h-12 rounded-xl object-contain bg-white border border-stone-200 p-1 flex items-center justify-center overflow-hidden">
                      {c.logo_url ? <img src={c.logo_url} alt={c.name} className="w-full h-full object-contain"/> : <span className="font-display font-bold text-stone-400 text-lg">{c.name[0]}</span>}
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2"><h3 className="font-display text-xl font-semibold text-stone-900">{c.name}</h3><PlanPill plan={c.plan}/></div>
                      <p className="text-sm text-stone-500 flex items-center gap-1"><MapPin size={12}/> {c.address} · {c.city_slug}</p>
                    </div>
                  </div>
                  <p className="text-sm text-stone-600 mt-1">{c.description}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {c.plan === "OURO" && <OuroBadge/>}
                    {c.accepts_cashback && (c.plan === "PRATA" || c.plan === "OURO") && <CashbackBadge/>}
                  </div>
                  {c.plan_expires_at && <p className="text-[11px] text-stone-400 mt-2">Vigência até: {new Date(c.plan_expires_at).toLocaleDateString("pt-BR")}</p>}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })} className="rounded-lg" data-testid={`merchant-edit-${c.id}`}>
                      <Edit size={13} className="mr-1"/> Editar dados, fotos e vídeos
                    </Button>
                    <a href={`/company/${c.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-blue-900 hover:text-blue-950 px-2 py-1.5 rounded-lg hover:bg-blue-50">Ver página pública →</a>
                  </div>
                </div>
                <div className="flex flex-col gap-2 min-w-[180px]">
                  <p className="text-xs text-stone-500 font-semibold uppercase tracking-wider">
                    {c.plan === "GRATIS" ? "Assinar um plano" : "Mudar de plano"}
                  </p>
                  {c.pending_plan && (
                    <div className="text-[11px] p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                      <strong>Downgrade agendado:</strong> seu plano mudará para <strong>{c.pending_plan}</strong> ao expirar.
                    </div>
                  )}
                  {plans.map((p) => {
                    const isCurrent = c.plan === p.tier;
                    return (
                      <Button
                        key={p.tier}
                        size="sm"
                        disabled={busy || isCurrent}
                        onClick={() => {
                          if (c.plan === "GRATIS") {
                            // First subscription
                            openPlanDialog(c.id, p);
                          } else {
                            // Change plan flow (upgrade pro-rata or downgrade scheduled)
                            setChangePlanFor({ company: c, plan: p });
                          }
                        }}
                        className={`w-full rounded-lg text-xs ${isCurrent ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-stone-900 hover:bg-stone-800 text-white"}`}
                        data-testid={`upgrade-mp-${c.id}-${p.tier}`}
                      >
                        {isCurrent ? `✓ Atual · ${p.display_name || p.tier}` : `${p.display_name || p.tier} · R$ ${p.price.toFixed(2)}`}
                      </Button>
                    );
                  })}
                  <Link to={`/merchant/subscriptions/${c.id}`} className="text-[11px] text-blue-900 hover:text-blue-950 hover:underline mt-1 text-center">
                    Gerenciar assinaturas →
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="wallet" className="mt-6 space-y-6">
          {companies.length === 0 && <p className="text-stone-500">Cadastre uma empresa primeiro.</p>}
          {visibleCompanies.map((c) => (
            <div key={c.id}>
              <h3 className="font-display text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                <PlanPill plan={c.plan}/> {c.name}
              </h3>
              <CompanyWallet company={c}/>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="cashback" className="mt-6 space-y-4">
          <Card className="p-6 rounded-2xl border-stone-200">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Coins size={20}/> Conceder cashback ao cliente</h2>
            <p className="text-sm text-stone-500 mt-1">O cashback é debitado do saldo da carteira da sua empresa e creditado instantaneamente na carteira do cliente. Sem taxa nessa operação.</p>
            <form onSubmit={registerCashback} className="grid sm:grid-cols-3 gap-4 mt-5">
              <div>
                <Label>Código da carteira do cliente</Label>
                <div className="flex gap-2 mt-1">
                  <Input required value={txForm.wallet_code} onChange={(e) => setTxForm({...txForm, wallet_code: e.target.value})} placeholder="MB-XXXX-XXXX"
                    className="rounded-xl font-mono tracking-wider uppercase" data-testid="tx-wallet-code"/>
                  <Button type="button" onClick={() => setScanFor("cashback")} variant="outline" className="rounded-xl shrink-0" data-testid="tx-scan-btn" title="Escanear QR">
                    <Camera size={14}/>
                  </Button>
                  <Button type="button" onClick={() => lookupCustomer()} disabled={!txForm.wallet_code} variant="outline" className="rounded-xl shrink-0">
                    <Search size={14}/>
                  </Button>
                </div>
              </div>
              <div>
                <Label>Empresa</Label>
                <Select value={txForm.company_id} onValueChange={(v) => { setTxForm({...txForm, company_id: v}); refreshPreview(v, txForm.purchase_value); }}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="tx-company"><SelectValue placeholder="Selecione"/></SelectTrigger>
                  <SelectContent>{visibleCompanies.filter((c) => c.plan === "PRATA" || c.plan === "OURO" || c.plan === "PLATINUM").map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.plan} · {c.cashback_rate_pct || 5}%)</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor da compra (R$)</Label>
                <Input required type="number" step="0.01" min="0.01" value={txForm.purchase_value} onChange={(e) => { setTxForm({...txForm, purchase_value: e.target.value}); refreshPreview(txForm.company_id, e.target.value); }} className="rounded-xl mt-1" data-testid="tx-amount"/>
              </div>
              {txCustomer && (
                <div className="sm:col-span-3 p-3 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-between" data-testid="tx-customer-info">
                  <div>
                    <div className="text-sm font-semibold text-blue-950">{txCustomer.name}</div>
                    <div className="text-[11px] text-stone-600 font-mono">{txCustomer.wallet_code}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Saldo cliente</div>
                    <div className="font-display text-lg font-bold text-blue-950">R$ {txCustomer.balance.toFixed(2)}</div>
                  </div>
                </div>
              )}
              {txPreview && (
                <div className="sm:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200" data-testid="tx-preview">
                  <div><div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Cashback ao cliente</div><div className="font-display text-xl text-blue-950">R$ {txPreview.cashback_value.toFixed(2)}</div><div className="text-[11px] text-stone-500">{txPreview.cashback_rate_pct}% da compra</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-stone-600 font-bold">Saldo da empresa</div><div className="font-display text-xl text-stone-700">R$ {txPreview.company_wallet_balance.toFixed(2)}</div></div>
                  <div className="col-span-2 sm:col-span-1"><div className="text-[10px] uppercase tracking-wider font-bold">Status</div>
                    {txPreview.insufficient
                      ? (
                        <div className="flex flex-col gap-1">
                          <div className="font-display text-base text-red-700">Saldo insuficiente</div>
                          <Link to="/merchant/packages" className="text-xs font-bold text-blue-900 hover:underline" data-testid="recharge-wallet-link">
                            💰 Recarregar Carteira →
                          </Link>
                        </div>
                      )
                      : <div className="font-display text-base text-emerald-700">Pronto para conceder</div>}
                  </div>
                </div>
              )}
              <div className="sm:col-span-3 flex justify-end">
                <Button type="submit" disabled={busy || !txCustomer || txPreview?.insufficient} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="tx-submit">{busy ? "Processando..." : "Conceder cashback"}</Button>
              </div>
            </form>
          </Card>

          <Card className="p-6 rounded-2xl border-stone-200">
            <h3 className="font-display text-lg font-semibold mb-3">Histórico de cashback concedido ({pending.length})</h3>
            <div className="divide-y divide-stone-100">
              {pending.length === 0 && <p className="text-sm text-stone-500">Nenhum cashback concedido ainda.</p>}
              {pending.map((t) => (
                <div key={t.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`pending-tx-${t.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-sm font-medium text-stone-900">{t.company_name} · {t.customer_name || "Cliente"}</div>
                    <div className="text-[11px] text-stone-500">{new Date(t.created_at).toLocaleString("pt-BR")} · compra R$ {t.purchase_value.toFixed(2)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg font-bold text-stone-900">R$ {t.cashback_value.toFixed(2)}</div>
                  </div>
                  <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">Pago</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="vouchers" className="mt-6">
          <MerchantVouchers companies={visibleCompanies} onRefresh={refresh} />
        </TabsContent>

        <TabsContent value="redeem" className="mt-6">
          <MerchantRedeem companies={visibleCompanies}/>
        </TabsContent>

        <TabsContent value="products" className="mt-6 space-y-6">
          {companies.length === 0 && <p className="text-stone-500">Cadastre uma empresa primeiro.</p>}
          {visibleCompanies.map((c) => (
            <div key={c.id} className="border border-stone-200 rounded-2xl p-5 bg-white">
              <MerchantProducts company={c}/>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <MerchantReports />
        </TabsContent>

        <TabsContent value="dev" className="mt-6 space-y-6">
          {companies.length === 0 && <p className="text-stone-500">Cadastre uma empresa primeiro.</p>}
          {visibleCompanies.map((c) => (
            <div key={c.id} className="border border-stone-200 rounded-2xl p-5 bg-white">
              <MerchantDevApi company={c}/>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {editing && (
        <CompanyEditDialog
          company={editing}
          setCompany={setEditing}
          cities={cities}
          busy={busy}
          isAdmin={user?.role === "ADMIN"}
          onClose={() => setEditing(null)}
          onSave={async () => {
            setBusy(true);
            try {
              const payload = {
                name: editing.name, description: editing.description, category: editing.category,
                city_slug: editing.city_slug, address: editing.address,
                lat: parseFloat(editing.lat), lng: parseFloat(editing.lng),
                phone: editing.phone || null, whatsapp: editing.whatsapp || null,
                instagram: editing.instagram || null, website: editing.website || null,
                logo_url: editing.logo_url || null,
                cover_url: editing.cover_url || null,
                segments: editing.segments || [],
                media: editing.media || [],
                cnpj: editing.cnpj ? String(editing.cnpj).replace(/[^0-9]/g, "") : null,
                pix_key: editing.pix_key || null,
                has_delivery: !!editing.has_delivery,
                pickup_available: editing.pickup_available !== false,
                business_hours: editing.business_hours || null,
                branches: (editing.branches || []).filter((b) => b && (b.address || b.lat)).map((b) => ({
                  id: b.id,
                  name: (b.name || "").trim(),
                  address: (b.address || "").trim(),
                  lat: parseFloat(b.lat),
                  lng: parseFloat(b.lng),
                  phone: b.phone_same ? null : (b.phone || null),
                  whatsapp: b.whatsapp_same ? null : (b.whatsapp || null),
                  instagram: b.instagram_same ? null : (b.instagram || null),
                  business_hours: b.business_hours_same ? null : (b.business_hours || null),
                  active: b.active !== false,
                })),
              };
              // Apenas Admin pode alterar campos sensíveis
              if (user?.role === "ADMIN") {
                payload.accepts_cashback = !!editing.accepts_cashback;
                payload.cashback_rate_pct = parseFloat(editing.cashback_rate_pct ?? 5);
                payload.active = editing.active !== false;
              }
              await api.patch(`/companies/${editing.id}`, payload);
              toast.success("Empresa atualizada");
              setEditing(null);
              refresh();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            finally { setBusy(false); }
          }}
        />
      )}

      {scanFor === "cashback" && (
        <QrScanner
          onClose={() => setScanFor(null)}
          onScan={(text) => {
            setScanFor(null);
            setTxForm((s) => ({ ...s, wallet_code: text }));
            lookupCustomer(text);
          }}
        />
      )}

      {planDialog && (
        <PlanChoiceDialog
          companyId={planDialog.companyId}
          plan={planDialog.plan}
          onClose={closePlanDialog}
          onOneTime={() => { closePlanDialog(); subscribe(planDialog.companyId, planDialog.plan.tier); }}
          onRecurring={() => { closePlanDialog(); subscribeRecurring(planDialog.companyId, planDialog.plan.tier); }}
          busy={busy}
        />
      )}

      {changePlanFor && (
        <ChangePlanDialog
          company={changePlanFor.company}
          plan={changePlanFor.plan}
          onClose={() => setChangePlanFor(null)}
          onDone={() => { setChangePlanFor(null); refresh(); }}
        />
      )}
    </div>
  );
}

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { RefreshCw, CreditCard as CardIcon } from "lucide-react";

function PlanChoiceDialog({ plan, onClose, onOneTime, onRecurring, busy }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="plan-choice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Plano {plan.display_name || plan.tier}
            <span className="block text-base font-normal text-stone-500 mt-1">R$ {plan.price.toFixed(2)} / mês</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-stone-600">Escolha como deseja pagar este plano:</p>

          <Card className="p-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 cursor-pointer transition" onClick={busy ? null : onRecurring} data-testid="choose-recurring">
            <div className="flex items-start gap-3">
              <RefreshCw className="text-emerald-700 shrink-0 mt-0.5" size={22}/>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-base font-bold text-emerald-900">Assinatura Recorrente</h3>
                  <span className="text-[10px] font-bold bg-emerald-700 text-white px-2 py-0.5 rounded-full">RECOMENDADO</span>
                </div>
                <p className="text-xs text-emerald-800 mt-1">
                  Cartão de crédito · cobrança automática mensal. Sem se preocupar com renovação. Pode cancelar a qualquer momento.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl border border-stone-200 hover:bg-stone-50 cursor-pointer transition" onClick={busy ? null : onOneTime} data-testid="choose-onetime">
            <div className="flex items-start gap-3">
              <CardIcon className="text-stone-700 shrink-0 mt-0.5" size={22}/>
              <div className="flex-1">
                <h3 className="font-display text-base font-bold text-stone-900">Pagamento Único (mensal)</h3>
                <p className="text-xs text-stone-600 mt-1">
                  PIX ou cartão · paga 1 mês de cada vez. Você precisa renovar manualmente todo mês.
                </p>
              </div>
            </div>
          </Card>

          <Button variant="outline" onClick={onClose} className="w-full rounded-xl" data-testid="plan-dialog-cancel">
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChangePlanDialog({ company, plan, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.post("/subscriptions/change-plan/preview", { company_id: company.id, new_plan_tier: plan.tier })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscriptions/change-plan", { company_id: company.id, new_plan_tier: plan.tier });
      if (data.type === "upgrade" && data.init_point) {
        toast.success("Redirecionando ao Mercado Pago para pagar a diferença...");
        window.location.href = data.init_point;
      } else if (data.type === "downgrade") {
        toast.success(`Downgrade agendado para ${data.scheduled_for ? new Date(data.scheduled_for).toLocaleDateString("pt-BR") : "o fim do ciclo atual"}`);
        onDone();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="change-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {preview?.type === "upgrade" ? "Upgrade" : preview?.type === "downgrade" ? "Downgrade" : "Mudança de plano"}
            <span className="block text-base font-normal text-stone-500 mt-1">
              {company.plan} → {plan.tier}
            </span>
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-stone-500">Calculando...</div>}
        {!loading && preview && (
          <div className="space-y-4">
            {preview.type === "upgrade" ? (
              <Card className="p-4 rounded-xl bg-emerald-50 border-emerald-200">
                <h3 className="font-display text-base font-bold text-emerald-900 mb-2">Upgrade imediato (pró-rata)</h3>
                <ul className="text-sm text-emerald-900 space-y-1.5">
                  <li>· Plano atual: <strong>{preview.current_plan}</strong> (R$ {preview.current_price.toFixed(2)}/mês)</li>
                  <li>· Novo plano: <strong>{preview.new_plan}</strong> (R$ {preview.new_price.toFixed(2)}/mês)</li>
                  <li>· Dias restantes do plano atual: <strong>{preview.days_left_current}</strong></li>
                  <li>· Crédito pró-rata aplicado: <strong>R$ {preview.unused_credit.toFixed(2)}</strong></li>
                </ul>
                <div className="mt-3 p-3 rounded-lg bg-white border border-emerald-300">
                  <p className="text-xs text-stone-500">Valor a pagar agora</p>
                  <p className="font-display text-3xl font-bold text-emerald-700" data-testid="upgrade-amount">R$ {preview.amount_to_pay.toFixed(2)}</p>
                </div>
                <p className="text-xs text-emerald-800 mt-3">{preview.message}</p>
              </Card>
            ) : (
              <Card className="p-4 rounded-xl bg-amber-50 border-amber-200">
                <h3 className="font-display text-base font-bold text-amber-900 mb-2">Downgrade agendado</h3>
                <ul className="text-sm text-amber-900 space-y-1.5">
                  <li>· Plano atual: <strong>{preview.current_plan}</strong></li>
                  <li>· Novo plano: <strong>{preview.new_plan}</strong> (R$ {preview.new_price.toFixed(2)}/mês)</li>
                  <li>· Dias restantes: <strong>{preview.days_left_current}</strong></li>
                </ul>
                <p className="text-xs text-amber-800 mt-3">{preview.message}</p>
                <p className="text-[11px] text-amber-700 mt-2 italic">⚠️ Sem estorno do valor pago. Você aproveita até o fim do ciclo atual.</p>
              </Card>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="cancel-change-plan">Cancelar</Button>
              <Button onClick={confirm} disabled={submitting} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="confirm-change-plan">
                {submitting ? "Processando..." : preview.type === "upgrade" ? `Pagar R$ ${preview.amount_to_pay.toFixed(2)}` : "Agendar Downgrade"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
